import { ConnectionItem, Flex, Image, Link, Text } from "@wayflyer/flyui";
import { useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { connectByPath } from "../../../data-sources";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { AuthCallbackPath } from "../../callback-paths";
import { usePlatformConnectorProperties } from "../../queries";
import { authStartPath, useConnectionFlowsConfig } from "../../config";
import { ConnectionsList } from "../../common/connections";
import { OAuthErrorBanner } from "../../common/oauth-error-banner";
import { MESSAGES } from "../../messages";

import { SHOPIFY_MESSAGES } from "./message-definitions";
import { ShopifyInitialConnection } from "./shopify-initial-connection";

const CONNECTOR_SLUG = "shopify-appstore";

interface ShopifyAppStoreConnectProps {
  postConnectPath: AuthCallbackPath;
  fullWidthImage?: boolean;
  onInitiateConnection?: () => void;
}

export const ShopifyAppStoreConnect = ({
  postConnectPath,
  fullWidthImage,
  onInitiateConnection,
}: ShopifyAppStoreConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { connectorsApiUrl } = useConnectionFlowsConfig();
  const { data: connectorProperties } = usePlatformConnectorProperties(
    customerDomain,
    CONNECTOR_SLUG,
    postConnectPath,
    connectorsApiUrl
  );
  const [connectionInProgress, setConnectionInProgress] = useState(false);

  const connectHandler = async () => {
    setConnectionInProgress(true);
    onInitiateConnection?.();
    await connectByPath(authStartPath(customerDomain, CONNECTOR_SLUG, postConnectPath));
  };

  return (
    <Flex direction="column" gap="8" align="flex-start" justify="space-between">
      <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
      <Text>{intl.formatMessage(SHOPIFY_MESSAGES.shopifyDataUsage)}</Text>
      {connectorProperties && !!connectorProperties.connections.length ? (
        <ConnectionsList
          connectorProperties={connectorProperties}
          renderConnectAnother={(onConnectAnother) => (
            <Text size="sm">
              {intl.formatMessage(MESSAGES.connectAnotherStore, {
                link: (chunks: ReactNode) => <Link onClick={onConnectAnother}>{chunks}</Link>,
              })}
            </Text>
          )}
          renderConnectAnotherHelp={() => (
            <Flex direction="column" gap="1">
              <Text size="sm" fontWeight="semiBold">
                {intl.formatMessage(MESSAGES.multipleAccountsHelperTitle)}
              </Text>
              <Text size="sm">
                {intl.formatMessage(MESSAGES.multipleAccountsHelperBody, {
                  link: (chunks: ReactNode) => (
                    <Link
                      url="https://help-center.wayflyer.com/en/articles/210531-how-do-i-connect-my-shopify-store"
                      external
                    >
                      {chunks}
                    </Link>
                  ),
                })}
              </Text>
            </Flex>
          )}
          connectForm={
            <Flex direction="column">
              <ConnectionItem
                key="shopify"
                label="Shopify"
                logo={
                  connectorProperties.connectionLogoSrc ? <Image src={connectorProperties.connectionLogoSrc} /> : null
                }
                loading={connectionInProgress}
                actions={[
                  {
                    onAction() {
                      connectHandler();
                    },
                    variant: "Primary",
                    content: intl.formatMessage(SHOPIFY_MESSAGES.shopifyConnectStoreLabel),
                  },
                ]}
              />
            </Flex>
          }
        />
      ) : (
        <ShopifyInitialConnection connectHandler={connectHandler} fullWidthImage={fullWidthImage} />
      )}
    </Flex>
  );
};
